import { Button, Select, Space, Typography } from 'antd'
import moment from 'moment'
import { CalendarOutlined, ThunderboltOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { getFormattedDate, TIME_FORMAT } from '../../../utils/dates'
import { useEffect, useMemo, useState } from 'react'
import EmptyState from '../../../kernel/infrastructure/empty-state.component'
import { Slot } from '../domain/job'

function DeliverySchedule({ showEmptyErrors = false, slots, onChange, timezone }: DeliveryScheduleProps) {
  const {
    t,
    i18n: { language: languageWithRegion }
  } = useTranslation()

  const [deliverySchedule, setDeliverySchedule] = useState<{
    date?: string
    slot?: Slot
    type?: typeof SCHEDULE_APPOINTMENT_TYPE[number]
  }>({})

  const type = useMemo(() => deliverySchedule.type, [deliverySchedule.type])
  const slot = useMemo(() => deliverySchedule.slot, [deliverySchedule.slot])
  const slotDate = useMemo(() => deliverySchedule.date, [deliverySchedule.date])

  const language = languageWithRegion.split('-')[0]
  moment.locale(language)

  const staticsSlots = useMemo(
    () =>
      groupByDate(slots?.static || [], timezone).map(({ slots, date }) => ({
        date,
        slots
      })),
    [slots?.static]
  )

  const expressSlots = useMemo(
    () =>
      groupByDate(slots?.express || [], timezone).map(({ slots, date }) => ({
        date,
        slots
      })),
    [slots?.express]
  )

  const hasStaticSlots = useMemo(() => staticsSlots.length > 0, [staticsSlots])
  const hasExpressSlots = useMemo(() => expressSlots.length > 0, [expressSlots])
  const slotsInterface = useMemo(
    () => type && (type === 'EXPRESS' ? expressSlots : staticsSlots),
    [type, expressSlots, staticsSlots]
  )

  useEffect(() => {
    if (hasStaticSlots && !hasExpressSlots) {
      setDeliverySchedule({
        type: 'STATIC'
      })
    } else if (hasExpressSlots && !hasStaticSlots) {
      setDeliverySchedule({
        type: 'EXPRESS'
      })
    } else {
      setDeliverySchedule({})
    }
  }, [slots])

  useEffect(() => {
    const dateSlots = slotDate && slotsInterface.find(({ date }) => date === slotDate)?.slots

    if (type && slotDate && !slot && dateSlots?.length === 1) {
      setDeliverySchedule({
        type,
        date: slotDate,
        slot: dateSlots[0] as Slot
      })
      return
    }

    onChange({ date: slotDate, slot, type })
  }, [type, slotDate, slot, slotsInterface])

  return (
    <>
      {hasStaticSlots || hasExpressSlots ? (
        <Space
          direction='vertical'
          size='middle'
          style={{
            width: '100%'
          }}
        >
          {showEmptyErrors && !type && (
            <Text type='danger'>{t('jobs.rejectItemsView.deliverySchedule.emptyTypeError')}</Text>
          )}
          <Space direction='horizontal' size='small'>
            {hasStaticSlots && (
              <CustomButton
                type={type === 'STATIC' ? 'primary' : 'default'}
                icon={<CalendarOutlined />}
                selected={type === 'STATIC'}
                onClick={() =>
                  setDeliverySchedule({
                    type: 'STATIC'
                  })
                }
              >
                {t('jobs.rejectItemsView.deliverySchedule.static')}
              </CustomButton>
            )}
            {hasExpressSlots && (
              <CustomButton
                type={type === 'EXPRESS' ? 'primary' : 'default'}
                selected={type === 'EXPRESS'}
                icon={<ThunderboltOutlined />}
                onClick={() =>
                  setDeliverySchedule({
                    type: 'EXPRESS'
                  })
                }
              >
                {t('jobs.rejectItemsView.deliverySchedule.express')}
              </CustomButton>
            )}
          </Space>

          {showEmptyErrors && !slotDate && (
            <Text type='danger'>{t('jobs.rejectItemsView.deliverySchedule.emptyDateError')}</Text>
          )}
          <DaysBoxContainer>
            {slotsInterface?.map(({ date, slots }) => (
              <DayBox
                key={date}
                selected={slotDate === date}
                onClick={() =>
                  setDeliverySchedule((_deliverySchedule) => ({
                    type: _deliverySchedule.type,
                    date
                  }))
                }
              >
                <PrimaryText strong>
                  {getFormattedDate({
                    date: slots[0].from,
                    format: 'ddd',
                    timezoneIn: 'UTC',
                    timezoneOut: timezone
                  })}
                </PrimaryText>
                <NoWrapText type='secondary'>
                  {getFormattedDate({
                    date: slots[0].from,
                    format: 'MMM DD',
                    timezoneIn: 'UTC',
                    timezoneOut: timezone
                  })}
                </NoWrapText>
              </DayBox>
            ))}
          </DaysBoxContainer>
          {showEmptyErrors && !slot && (
            <Text type='danger'>{t('jobs.rejectItemsView.deliverySchedule.emptySlotError')}</Text>
          )}
          {slotDate && (
            <SlotsSelect
              defaultValue='Seleccione el slot'
              value={
                slot ? `${slot.from} - ${slot.to}` : `${t('jobs.rejectItemsView.deliverySchedule.selectSlotDefault')}`
              }
              onChange={(value, option) => {
                const [from, to] = value!.toString().split(' - ')
                const operationalModel = option['data-operational-model']
                const id = option['data-id']
                setDeliverySchedule((_deliverySchedule) => ({
                  ..._deliverySchedule,
                  slot: {
                    id,
                    from,
                    to,
                    operationalModel
                  }
                }))
              }}
            >
              {slotsInterface
                ?.find(({ date }) => date === slotDate)
                ?.slots.map((slot) => {
                  const { from, to } = slot
                  const id = slot?.id
                  const operationalModel = slot?.operationalModel
                  return (
                    <Option
                      key={`${id}-${operationalModel}-${slotDate}-${from}-${to}`}
                      data-id={id}
                      data-operational-model={operationalModel}
                      value={`${from} - ${to}`}
                    >
                      {`${getFormattedDate({
                        date: from,
                        timezoneIn: 'UTC',
                        timezoneOut: timezone,
                        format: TIME_FORMAT
                      })} - ${getFormattedDate({
                        date: to,
                        timezoneIn: 'UTC',
                        timezoneOut: timezone,
                        format: TIME_FORMAT
                      })}`}
                    </Option>
                  )
                })}
            </SlotsSelect>
          )}
        </Space>
      ) : (
        <EmptyState
          title={t('jobs.rejectItemsView.deliverySchedule.noScheduleAvailable')}
          subtitle={t('jobs.rejectItemsView.deliverySchedule.configureYourSlots')}
        />
      )}
    </>
  )
}

export default DeliverySchedule

function groupByDate(
  slots: {
    from: string
    to: string
    id?: string
    operationalModel?: string
  }[],
  timezone: string
) {
  const result: {
    date: string
    slots: { from: string; to: string; id?: string; operationalModel?: string }[]
  }[] = []

  slots.forEach((slot) => {
    const date = moment(slot.from).tz(timezone).format('YYYY-MM-DD')
    const existingGroup = result.find((group) => group.date === date)

    if (existingGroup) {
      existingGroup.slots.push({ from: slot.from, to: slot.to, id: slot?.id, operationalModel: slot?.operationalModel })
    } else {
      result.push({
        date,
        slots: [slot]
      })
    }
  })

  return result
}

type DeliveryScheduleProps = {
  timezone: string
  showEmptyErrors?: boolean
  slots: {
    express?: Slot[]
    static?: Slot[]
  }
  onChange: ({
    date,
    slot,
    type
  }: {
    date?: string
    slot?: Slot
    type?: typeof SCHEDULE_APPOINTMENT_TYPE[number]
  }) => void
}

const SCHEDULE_APPOINTMENT_TYPE = ['EXPRESS', 'STATIC'] as const
const MAIN_COLOR = '#1777FF'

const { Text } = Typography
const { Option } = Select

const SlotsSelect = styled(Select)`
  width: 100%;
`

const PrimaryText = styled(Text)`
  color: ${MAIN_COLOR} !important;
`

const DaysBoxContainer = styled.div`
  display: flex;
  width: fit-content;
  justify-content: flex-start;
`

const CustomButton = styled(Button)<{
  selected: boolean | undefined
}>`
  background: ${({ selected }) => (selected ? '#E6F4FF !important' : 'white !important')};
  border: 1px solid ${({ selected }) => (selected ? `${MAIN_COLOR} !important` : '#e9edf3 !important')};
  color: ${({ selected }) => (selected ? `${MAIN_COLOR} !important` : 'black !important')};
  &:hover {
    border-color: ${MAIN_COLOR} !important;
    color: ${MAIN_COLOR} !important;
  }
`

const DayBox = styled.div<{ selected: boolean; disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  padding: 10px;
  margin: 0 3px;
  border: 1px solid ${({ selected }) => (selected ? MAIN_COLOR : '#e9edf3')};
  height: fit-content;
  background: ${({ selected }) => (selected ? '#E6F4FF' : 'white')};
  &:hover {
    ${({ disabled = false }) =>
      !disabled &&
      `
      border-color: ${MAIN_COLOR} !important;
      cursor: pointer;
    `}
  }
`

const NoWrapText = styled(Text)`
  white-space: nowrap;
`
